import * as React from "react";
import { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import {
  Grid,
  TextField,
  Typography,
  Button,
  Tabs,
  Tab,
  Box,
  Dialog,
  DialogContent,
} from "@mui/material";
import Title from "../Title";
import MultiValueInput from "../../inputs/multiValueInput";
import MultiSelector from "../../inputs/MultiSelector";
import SearchDateInput from "../../inputs/searchDateInput";
import {
  setParameters,
  clearParameters,
  removeParameter,
  setDateParameters,
} from "../../redux/global/actions";
import {
  getSfdcSynchronizerMonitoring,
  getSfdcSynchronizerMonitoringBulk,
  filterSfdcSynchronizerMonitoring,
  triggerSfdcSync,
} from "../../redux/dynamo_db/actions";
import {
  extractUserInfoFromToken,
  getLocalStorageSAMLToken,
} from "../../utils";
import getUserProfiles from "../../tokenUtils";
import IconButton from "@mui/material/IconButton";
import { MaterialSymbol } from "@datalake/towercontrol-banner";
const DETAIL_TYPE_VALUES = [
  "rolling_accreditation",
  "prm_detail",
  "asset",
  "contract",
];

const CONTRACT_OBJECT_TYPES = [
  "Asset Tracking",
  "ClearPass Support Basic",
  "ClearPass Support Software",
  "Complimentary Services",
  "BP Support Plus",
  "BP Long Term Support Plus",
  "BP Support Software",
  "BP Long Term Support Software",
  "BP Extended Support Software",
  "BP Long Term Support",
  "EC Support Plus",
  "EC Long Term Support Plus",
  "EC Support Total",
  "EC Support Software",
  "EC Long Term Support Software",
  "EC Extended Support Software",
  "EC Long Term Support",
  "Dead on arrival",
  "OTEC",
  "FLEX",
  "NaaS",
  "NaaS-OVC-4",
  "NECO EC Support Plus",
  "NECO EC Support Software",
  "NECO BP Support Plus",
  "NECO BP Support Software",
  "OVC Base",
  "OVC Business",
  "OVC Premium",
  "OVNG",
  "OVC10-CAPEX",
  "Purple",
  "PurpleEvergreen",
  "SA-OXO",
  "SPS",
  "SES",
  "SMS",
  "ProServ",
  "Standard Hardware Support RTF",
  "Standard Hardware Support AVR",
  "Support Basic",
];

const ASSET_OBJECT_TYPES = ["Serial Number", "SYSTEM_ID", "License"];

function getObjectTypeOptions(detailTypes = []) {
  const options = new Set();
  if (detailTypes.includes("asset"))
    ASSET_OBJECT_TYPES.forEach((v) => options.add(v));
  if (detailTypes.includes("contract"))
    CONTRACT_OBJECT_TYPES.forEach((v) => options.add(v));
  return [...options];
}

export default function SfdcSynchronizerMonitoring() {
  const dispatch = useDispatch();

  const elementIds = useSelector(
    (state) => state?.parameters?.sfdcSynchronizerMonitoring?.elementIds,
  );
  const detailTypes = useSelector(
    (state) => state?.parameters?.sfdcSynchronizerMonitoring?.detailTypes,
  );
  const sources = useSelector(
    (state) => state?.parameters?.sfdcSynchronizerMonitoring?.sources,
  );
  const objectTypes = useSelector(
    (state) => state?.parameters?.sfdcSynchronizerMonitoring?.objectTypes,
  );
  const startDate = useSelector(
    (state) => state?.parameters?.sfdcSynchronizerMonitoring?.startDate,
  );
  const endDate = useSelector(
    (state) => state?.parameters?.sfdcSynchronizerMonitoring?.endDate,
  );

  const [currentElementId, setCurrentElementId] = React.useState();
  const [currentSource, setCurrentSource] = React.useState();
  const [selectedTab, setSelectedTab] = React.useState(0);
  const [openBulkConfirmation, setOpenBulkConfirmation] = React.useState(false);

  const [filterElementId, setFilterElementId] = React.useState("");
  const [filterDetailType, setFilterDetailType] = React.useState("");
  const [filterObjectType, setFilterObjectType] = React.useState("");
  const [filterErrorCode, setFilterErrorCode] = React.useState("");
  const [filterErrorMessage, setFilterErrorMessage] = React.useState("");
  const [filterSfdcWrite, setFilterSfdcWrite] = React.useState("");
  const [filterSource, setFilterSource] = React.useState("");

  const handleDeleteElementId = (chip) => () =>
    dispatch(
      removeParameter({ sfdcSynchronizerMonitoring: { elementIds: [chip] } }),
    );
  const handleClearElementIds = () =>
    dispatch(clearParameters({ sfdcSynchronizerMonitoring: "elementIds" }));
  const handleDeleteDetailType = (chip) => () =>
    dispatch(
      removeParameter({ sfdcSynchronizerMonitoring: { detailTypes: [chip] } }),
    );
  const handleClearDetailTypes = () =>
    dispatch(clearParameters({ sfdcSynchronizerMonitoring: "detailTypes" }));
  const handleDeleteSource = (chip) => () =>
    dispatch(
      removeParameter({ sfdcSynchronizerMonitoring: { sources: [chip] } }),
    );
  const handleClearSources = () =>
    dispatch(clearParameters({ sfdcSynchronizerMonitoring: "sources" }));
  const handleDeleteObjectType = (chip) => () =>
    dispatch(
      removeParameter({ sfdcSynchronizerMonitoring: { objectTypes: [chip] } }),
    );
  const handleClearObjectTypes = () =>
    dispatch(clearParameters({ sfdcSynchronizerMonitoring: "objectTypes" }));

  const tabs = [
    { label: "Element ID Search", index: "default" },
    { label: "Detail Type Search", index: "detail_type" },
    { label: "Source Search", index: "source" },
  ];

  const buildQueryObject = () => {
    const { index } = tabs[selectedTab];
    const queryObject = { index };
    if (index === "default") {
      if (!elementIds?.length) return null;
      queryObject.element_id = elementIds;
    } else if (index === "detail_type") {
      if (!detailTypes?.length) return null;
      queryObject.detail_type = detailTypes;
      if (objectTypes?.length) queryObject.object_type = objectTypes;
      if (startDate) queryObject.start_date = startDate;
      if (endDate) queryObject.end_date = endDate;
    } else if (index === "source") {
      if (!sources?.length) return null;
      queryObject.source = sources;
      if (startDate) queryObject.start_date = startDate;
      if (endDate) queryObject.end_date = endDate;
    }
    return queryObject;
  };

  useEffect(() => {
    dispatch(clearParameters({ sfdcSynchronizerMonitoring: "objectTypes" }));
  }, [detailTypes]);

  const handleExecuteSearch = () => {
    const q = buildQueryObject();
    if (q) dispatch(getSfdcSynchronizerMonitoring(q));
  };

  const handleExecuteBulkSearch = () => {
    const q = buildQueryObject();
    if (!q) return;
    const saml_token = getLocalStorageSAMLToken();
    const infoToken = extractUserInfoFromToken(saml_token);
    q.email = infoToken.email;
    q.request_user = infoToken.username;
    dispatch(getSfdcSynchronizerMonitoringBulk(q));
  };

  const handleExecuteFilter = () => {
    dispatch(
      filterSfdcSynchronizerMonitoring({
        elementId: filterElementId,
        detailType: filterDetailType,
        objectType: filterObjectType,
        errorCode: filterErrorCode,
        errorMessage: filterErrorMessage,
        sfdcWrite: filterSfdcWrite,
        source: filterSource,
      }),
    );
  };

  useEffect(() => {
    handleExecuteFilter();
  }, [
    filterElementId,
    filterDetailType,
    filterObjectType,
    filterErrorCode,
    filterErrorMessage,
    filterSfdcWrite,
    filterSource,
  ]);

  const handleExecuteSearchRef = React.useRef(handleExecuteSearch);
  useEffect(() => {
    handleExecuteSearchRef.current = handleExecuteSearch;
  });
  useEffect(() => {
    const handleKeydown = (event) => {
      if (event.key === "Enter") {
        event.stopPropagation();
        event.preventDefault();
        setTimeout(() => handleExecuteSearchRef.current(), 0);
      }
    };
    document.addEventListener("keydown", handleKeydown);
    return () => document.removeEventListener("keydown", handleKeydown);
  }, []);

  const rows = useSelector(
    (state) => state.filteredSfdcSynchronizerMonitoring ?? [],
  );

  const profiles = getUserProfiles() ?? [];
  const isAdmin = profiles.some((p) => p.includes("admin"));

  return (
    <React.Fragment>
      <Dialog open={openBulkConfirmation} maxWidth="xs" fullWidth>
        <DialogContent>
          <Typography variant="body2">
            This query could take a few minutes, are you sure you want to
            continue?
          </Typography>

          <Grid container style={{ justifyContent: "center", gap: "7%" }}>
            <Button
              style={{ color: "white", background: "var(--ale-primary)" }}
              onClick={() => {
                handleExecuteBulkSearch();
                setOpenBulkConfirmation(false);
              }}
            >
              Yes
            </Button>
            <Button
              style={{ color: "white", background: "var(--ale-primary)" }}
              onClick={() => setOpenBulkConfirmation(false)}
            >
              No
            </Button>
          </Grid>
        </DialogContent>
      </Dialog>

      <Box sx={{ width: "100%" }}>
        <Tabs value={selectedTab} onChange={(_, v) => setSelectedTab(v)}>
          {tabs.map((tab, i) => (
            <Tab key={i} label={tab.label} />
          ))}
        </Tabs>

        {/* Tab 0: Element ID */}
        <div role="tabpanel" hidden={selectedTab !== 0}>
          {selectedTab === 0 && (
            <Box sx={{ p: 3 }}>
              <MultiValueInput
                setCurrentParam={setCurrentElementId}
                setQueryParams={setParameters}
                currentParam={currentElementId}
                queryParams={elementIds}
                handleDelete={handleDeleteElementId}
                handleClear={handleClearElementIds}
                label="Element ID *"
                family="sfdcSynchronizerMonitoring"
                type="elementIds"
              />
              <Grid
                container
                style={{ justifyContent: "center", marginTop: "20px" }}
              >
                <Button
                  variant="contained"
                  onClick={handleExecuteSearch}
                  style={{ width: "15vw" }}
                >
                  Search
                </Button>
                <div style={{ paddingLeft: "0.2%" }} />
                <Button
                  variant="contained"
                  onClick={() => setOpenBulkConfirmation(true)}
                  style={{ width: "15vw" }}
                >
                  Bulk Export
                </Button>
              </Grid>
            </Box>
          )}
        </div>

        {/* Tab 1: Detail Type */}
        <div role="tabpanel" hidden={selectedTab !== 1}>
          {selectedTab === 1 && (
            <Box sx={{ p: 3 }}>
              <MultiSelector
                setQueryParams={setParameters}
                queryParams={detailTypes}
                handleDelete={handleDeleteDetailType}
                handleClear={handleClearDetailTypes}
                label="Detail Type *"
                family="sfdcSynchronizerMonitoring"
                type="detailTypes"
                options={DETAIL_TYPE_VALUES}
              />
              {getObjectTypeOptions(detailTypes).length > 0 && (
                <MultiSelector
                  setQueryParams={setParameters}
                  queryParams={objectTypes}
                  handleDelete={handleDeleteObjectType}
                  handleClear={handleClearObjectTypes}
                  label="Object Type (optional)"
                  family="sfdcSynchronizerMonitoring"
                  type="objectTypes"
                  options={getObjectTypeOptions(detailTypes)}
                />
              )}
              <SearchDateInput
                title="Date Range"
                setFilterDate={setDateParameters}
                family="sfdcSynchronizerMonitoring"
                type="startDate"
                type2="endDate"
              />
              <Grid
                container
                style={{ justifyContent: "center", marginTop: "20px" }}
              >
                <Button
                  variant="contained"
                  onClick={handleExecuteSearch}
                  style={{ width: "15vw" }}
                >
                  Search
                </Button>
                <div style={{ paddingLeft: "0.2%" }} />
                <Button
                  variant="contained"
                  onClick={() => setOpenBulkConfirmation(true)}
                  style={{ width: "15vw" }}
                >
                  Bulk Export
                </Button>
              </Grid>
            </Box>
          )}
        </div>

        {/* Tab 2: Source */}
        <div role="tabpanel" hidden={selectedTab !== 2}>
          {selectedTab === 2 && (
            <Box sx={{ p: 3 }}>
              <MultiValueInput
                setCurrentParam={setCurrentSource}
                setQueryParams={setParameters}
                currentParam={currentSource}
                queryParams={sources}
                handleDelete={handleDeleteSource}
                handleClear={handleClearSources}
                label="Source *"
                family="sfdcSynchronizerMonitoring"
                type="sources"
              />
              <SearchDateInput
                title="Date Range"
                setFilterDate={setDateParameters}
                family="sfdcSynchronizerMonitoring"
                type="startDate"
                type2="endDate"
              />
              <Grid
                container
                style={{ justifyContent: "center", marginTop: "20px" }}
              >
                <Button
                  variant="contained"
                  onClick={handleExecuteSearch}
                  style={{ width: "15vw" }}
                >
                  Search
                </Button>
                <div style={{ paddingLeft: "0.2%" }} />
                <Button
                  variant="contained"
                  onClick={() => setOpenBulkConfirmation(true)}
                  style={{ width: "15vw" }}
                >
                  Bulk Export
                </Button>
              </Grid>
            </Box>
          )}
        </div>
      </Box>

      <Title>SFDC Synchronizer Monitoring</Title>
      <Typography>
        {rows?.length
          ? rows.length === 100
            ? rows.length + " results, use bulk export for more"
            : rows.length + " results"
          : null}
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>
              <TextField
                label="Element Id"
                variant="standard"
                onChange={(e) => setFilterElementId(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
              />
            </TableCell>
            <TableCell>
              <TextField
                label="Detail Type"
                variant="standard"
                onChange={(e) => setFilterDetailType(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
              />
            </TableCell>
            <TableCell>
              <TextField
                label="Object Type"
                variant="standard"
                onChange={(e) => setFilterObjectType(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
              />
            </TableCell>
            <TableCell>
              <TextField
                label="Error Code"
                variant="standard"
                onChange={(e) => setFilterErrorCode(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
              />
            </TableCell>
            <TableCell>
              <TextField
                label="Error Message"
                variant="standard"
                onChange={(e) => setFilterErrorMessage(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
              />
            </TableCell>
            <TableCell>Job Creation Date</TableCell>
            <TableCell>
              <TextField
                label="SFDC Write"
                variant="standard"
                onChange={(e) => setFilterSfdcWrite(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
              />
            </TableCell>
            <TableCell>
              <TextField
                label="Source"
                variant="standard"
                onChange={(e) => setFilterSource(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
              />
            </TableCell>
            {isAdmin && <TableCell />}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => (
            <TableRow key={row?.id ?? i} hover>
              <TableCell>{row?.id}</TableCell>
              <TableCell>{row?.detail_type}</TableCell>
              <TableCell>{row?.object_type}</TableCell>
              <TableCell>{row?.error_code}</TableCell>
              <TableCell>{row?.error_message}</TableCell>
              <TableCell>{row?.job_creation_date}</TableCell>
              <TableCell>{row?.sfdc_write?.toString()}</TableCell>
              <TableCell>{row?.source}</TableCell>
              {isAdmin && (
                <TableCell align="right">
                  {(row?.detail_type === "asset" ||
                    row?.detail_type === "contract") && (
                    <IconButton
                      size="small"
                      title="Trigger SFDC synchronizer"
                      onClick={() =>
                        dispatch(
                          triggerSfdcSync(
                            row.id,
                            row.detail_type,
                            row.object_type,
                          ),
                        )
                      }
                    >
                      <MaterialSymbol name="sync" fontSize="small" />
                    </IconButton>
                  )}
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </React.Fragment>
  );
}
